import { get, noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { STATE_CODES } from '../../../../../constants'
import * as events from '../../../../../events'
import { ValidatorList } from '../../../../../models/types'
import * as DA from '../../../../../typeModules/events'
import Form from '../../../../../utilities/Form'
import { formatDateWithLeadingZeroes } from '../../../../../utilities/formatDate'
import {
  validateCheckrDriversLicenseByState,
  validators as sharedValidators,
} from '../../../../../utilities/validators'
import { BirthdateInputSplit } from '../../../../atoms/birthdate-input'
import t from '../../strings'
import { IdentificationDataType } from '../../types'

import Button from '@doordash/component-button'
import {
  IdentificationField,
  Select,
  TextField,
} from '@doordash/component-fields'
import { StackChildren } from '@doordash/design-language'

type Props = {
  needsLicenseInfo: boolean
  identificationData: IdentificationDataType
  setIdentificationData: React.Dispatch<React.SetStateAction<object>>
  setIsShowingEditModal?: React.Dispatch<React.SetStateAction<boolean>>
}

type ReduxProps = {
  trackEditBGCInfo?: (data: DA.EditBGCInfo) => Promise<void>
}

type IForm<P> = React.FunctionComponent<P> & {
  Validators?: ValidatorList
  Fields?: {
    [key: string]: string
  }
}

const BGCForm: IForm<Props & ReduxProps> = ({
  needsLicenseInfo,
  identificationData,
  setIdentificationData,
  setIsShowingEditModal,
  trackEditBGCInfo,
}) => {
  BGCForm.Fields = {
    ssn: identificationData.ssn || '',
    confirmSsn: identificationData.confirmSsn || '',
    birthday: identificationData.birthday || '',
    licenseNumber: identificationData.licenseNumber || '',
    licenseState: identificationData.licenseState || '',
  }

  BGCForm.Validators = {
    ssn: sharedValidators.ssn,
    confirmSsn: sharedValidators.confirmSsn,
    birthday: sharedValidators.birthday,
    licenseNumber: needsLicenseInfo
      ? validateCheckrDriversLicenseByState
      : noop,
    licenseState: needsLicenseInfo ? sharedValidators.licenseState : noop,
  }

  const handleFormSubmit = async data => {
    try {
      const processedData = Object.assign(data, {
        birthday: formatDateWithLeadingZeroes(get(data, 'birthday', null)),
      })

      setIdentificationData(processedData)
    } catch (e) {
      console.error(e)
    }
    setIsShowingEditModal(false)
  }

  return (
    <Form
      onSubmit={data => handleFormSubmit(data)}
      validators={BGCForm.Validators}
      fields={BGCForm.Fields}
    >
      {(fields, handleChange, handleSubmit, { isLoading }) => (
        <form onSubmit={handleSubmit}>
          <StackChildren size={StackChildren.Sizes.Large}>
            {needsLicenseInfo && (
              <React.Fragment>
                <Select
                  accessibilityDescribedBy={'licenseState'}
                  label={t.licenseStateLabel()}
                  description={t.required()}
                  disabledValue={t.licenseStateDisabled()}
                  onChange={handleChange.bind(this, 'licenseState')}
                  options={STATE_CODES}
                  {...fields.licenseState}
                />

                <TextField
                  accessibilityDescribedBy={'licenseNumber'}
                  label={t.licenseNumberLabel()}
                  description={t.required()}
                  onChange={handleChange.bind(this, 'licenseNumber')}
                  autoComplete="off"
                  {...fields.licenseNumber}
                />
              </React.Fragment>
            )}
            <BirthdateInputSplit
              id="birthdate-input-split"
              label={t.birthdate()}
              onChange={handleChange.bind(this, 'birthday')}
              birthdayField={fields.birthday}
              {...fields.birthday}
            />
            <IdentificationField
              country="US"
              accessibilityDescribedBy={'socialSecurityNumber'}
              onChange={handleChange.bind(this, 'ssn')}
              label={t.socialSecurityNumber()}
              description={t.required()}
              autoComplete="off"
              {...fields.ssn}
            />
            <IdentificationField
              country="US"
              accessibilityDescribedBy={'confirmSocialSecurityNumber'}
              onChange={handleChange.bind(this, 'confirmSsn')}
              label={t.confirmSocialSecurityNumber()}
              description={t.required()}
              autoComplete="off"
              {...fields.confirmSsn}
            />
            <Button
              accessibilityDescribedBy={'editBackgroundCheckInfo'}
              onClick={async () => {
                trackEditBGCInfo({
                  action: DA.EventActions.Submit,
                  name: DA.EditBGCInfoNames.SubmitButton,
                })
                try {
                  await handleSubmit()
                } catch (e) {
                  trackEditBGCInfo({
                    action: DA.EventActions.Error,
                    data: e,
                  })
                }
              }}
              state={isLoading ? Button.States.Loading : Button.States.Default}
            >
              {t.editDetailsPrimaryAction()}
            </Button>
          </StackChildren>
        </form>
      )}
    </Form>
  )
}

const mapDispatchToProps = {
  trackEditBGCInfo: events.trackEditBGCInfo,
}

export default connect(
  null,
  mapDispatchToProps
)(BGCForm)
